import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { StatCardConfig, ChartConfig } from "@hooks/use-reports/types";

export interface UserReportConfig {
  /** Row ID (integer) from the database — API may return this as 'row_id' */
  id: string | number;
  /** Always normalised to the report ID string (e.g. "inventoryAging") */
  report_id: string;
  user_id: number;
  is_active?: "0" | "1" | 0 | 1 | boolean;
  override_config: {
    charts?: Record<string, { hidden?: boolean; order?: number }>;
    statCards?: Record<string, { hidden?: boolean; order?: number }>;
    /** Custom stat cards added by user, stored with full configuration */
    addedStatCards?: Record<string, StatCardConfig>;
    /** Custom charts added by user, stored with full configuration */
    addedCharts?: Record<string, ChartConfig>;
    /** Report tab sort order override for this user-generated report */
    report_sort_order?: number;
  };
  updated_at?: string;
}

/**
 * Hook to fetch user's saved report configuration
 * Calls: GET /wp-json/smassets/v1/reports/user-config
 */
export const useGetReportUserConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const getUserConfig = useCallback(
    async (reportId: string): Promise<UserReportConfig | null> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl("wp-json/smassets/v1/reports/user-config");

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          if (response.status === 404) {
            // User config doesn't exist yet - this is normal
            setLoading(false);
            return null;
          }
          throw new Error(`Failed to fetch user config: ${response.status}`);
        }

        const rawData = await response.json();

        // Normalise the response — the API can return several shapes:
        //   { configs: [...] }          – standard array envelope
        //   [{ report_id, ... }, ...]   – bare array
        //   { report_id, ... }          – single object
        let allConfigs: any[];
        if (Array.isArray(rawData.configs)) {
          allConfigs = rawData.configs;
        } else if (Array.isArray(rawData)) {
          allConfigs = rawData;
        } else {
          allConfigs = [rawData].filter((c) => c?.report_id);
        }

        const config = allConfigs.find((c: any) => c.report_id === reportId || c.id === reportId);

        if (!config) {
          setLoading(false);
          return null;
        }

        // Normalise: the API sends the report ID string as 'id', not 'report_id'.
        // Always ensure report_id is set so callers can rely on it.
        const normalised: UserReportConfig = {
          ...config,
          report_id: config.report_id || (typeof config.id === "string" ? config.id : reportId),
        };

        setLoading(false);
        return normalised;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch user config";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  /**
   * Returns all user report configs without filtering by reportId.
   * Used to derive the global hidden-reports list on mount.
   */
  const getAllUserConfigs = useCallback(
    async (): Promise<UserReportConfig[]> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl("wp-json/smassets/v1/reports/user-config");

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          if (response.status === 404) {
            setLoading(false);
            return [];
          }
          throw new Error(`Failed to fetch user configs: ${response.status}`);
        }

        const rawData = await response.json();

        // Same normalisation as getUserConfig
        let allConfigs: any[];
        if (Array.isArray(rawData.configs)) {
          allConfigs = rawData.configs;
        } else if (Array.isArray(rawData)) {
          allConfigs = rawData;
        } else {
          allConfigs = [rawData].filter((c) => c?.report_id);
        }

        setLoading(false);
        return allConfigs as UserReportConfig[];
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch user configs";
        setError(errorMessage);
        setLoading(false);
        return [];
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return {
    getUserConfig,
    getAllUserConfigs,
    loading,
    error,
  };
};
